/**
 * Company KYC Mock Data Generator
 *
 * 该模块提供企业KYC公司资料表单的模拟数据生成功能
 * 使用 @faker-js/faker 生成符合表单验证规则的测试数据
 *
 * @module mockDataGenerator.company
 * @description 企业信息模拟数据生成器
 */

import {
  CompanyIndustryOptions,
  CompanyTypeOptions,
  ListingStatusOptions,
  TradingPurposeOptions,
} from "@/types/enum";
import { faker } from "@faker-js/faker";

/* ============================================================================
 * 类型定义
 * ========================================================================== */

/**
 * 国家数据类型
 */
interface CountryData {
  code2: string;
  cn: string;
  en: string;
  hk: string;
  tel: string;
  url: string;
  priority: number;
}

/**
 * 国家JSON数据结构
 */
interface CountryJsonData {
  code: string;
  msg: string;
  data: CountryData[];
}

// 导入真实的国家数据
import countryAllowedDataJson from "../../data/country-allowed.json";
import countryRestrictedDataJson from "../../data/country-restricted.json";

const countryAllowedData = countryAllowedDataJson as CountryJsonData;
const countryRestrictedData = countryRestrictedDataJson as CountryJsonData;

/**
 * 企业信息表单数据类型
 */
export interface CompanyInfoFormData {
  /** 公司名称（英文） */
  companyName: string;
  /** 公司名称（中文） */
  companyNameZh?: string;
  /** 银行账户名称 */
  bankAccountName: string;
  /** 香港商业登记号码 */
  hkBusinessRegNo: string;
  /** 上市状态 */
  listingStatus: ListingStatusOptions;
  /** 公司类型 */
  companyType: CompanyTypeOptions;
  /** 注册国家/地区 */
  registrationCountry: string;
  /** 注册日期 */
  registrationDate: string;
  /** 注册成立证书号码 */
  incorporationNo: string;
  /** 行业 */
  companyIndustry: CompanyIndustryOptions;
  /** 注册地址 */
  registeredAddress: string;
  /** 业务地址 */
  businessAddress?: string;
  /** 业务电话 */
  businessPhone: {
    phoneCountry: string;
    phoneNumber: string;
  };
  /** 电邮地址 */
  email: string;
  /** 企业网站 */
  website?: string;
  /** 交易目的 */
  purposeOfTransaction: TradingPurposeOptions[];
}

/**
 * Mock 数据生成配置选项
 */
export interface CompanyMockDataGeneratorOptions {
  /** 指定注册国家/地区 */
  registrationCountry?: string;
  /** 指定上市状态 */
  listingStatus?: ListingStatusOptions;
  /** 指定公司类型 */
  companyType?: CompanyTypeOptions;
  /** 指定行业 */
  companyIndustry?: CompanyIndustryOptions;
  /** 自定义种子 */
  seed?: number;
}

/* ============================================================================
 * 常量配置
 * ========================================================================== */

/**
 * 上市状态选项（来自 strapi-enum.ts）
 */
const LISTING_STATUS_OPTIONS = Object.values(ListingStatusOptions);

/**
 * 公司类型选项（来自 strapi-enum.ts）
 */
const COMPANY_TYPE_OPTIONS = Object.values(CompanyTypeOptions);

/**
 * 企业行业选项（来自 strapi-enum.ts）
 * 选择一些常用的行业
 */
const COMMON_COMPANY_INDUSTRY_OPTIONS = [
  CompanyIndustryOptions.Information_and_Communication,
  CompanyIndustryOptions.Financial_and_Insurance_Activities,
  CompanyIndustryOptions.Professional_Scientific_and_Technical_Services,
  CompanyIndustryOptions.Wholesale_and_Retail_Trade,
  CompanyIndustryOptions.Manufacturing,
  CompanyIndustryOptions.Real_Estate_Activities,
  CompanyIndustryOptions.Administrative_and_Support_Service_Activities,
  CompanyIndustryOptions.Education,
  CompanyIndustryOptions.Transportation_and_Storage,
  CompanyIndustryOptions.Construction,
];

/**
 * 交易目的选项（来自 strapi-enum.ts）
 */
const TRADING_PURPOSE_OPTIONS = Object.values(TradingPurposeOptions);

/**
 * 允许的国家列表（来自 country-allowed.json）
 */
const ALLOWED_COUNTRIES = countryAllowedData.data;

/**
 * 受限制的国家列表（来自 country-restricted.json）
 */
const RESTRICTED_COUNTRIES = countryRestrictedData.data;

/**
 * 所有可用国家列表（允许 + 受限制）
 * 企业KYC使用 COUNTRY_ALLOWED + COUNTRY_RESTRICTED
 */
const ALL_AVAILABLE_COUNTRIES = [...ALLOWED_COUNTRIES, ...RESTRICTED_COUNTRIES];

/**
 * 国家代码到电话区号的映射
 */
const COUNTRY_TEL_MAP = new Map(
  ALL_AVAILABLE_COUNTRIES.map((country) => [country.code2, country.tel])
);

/* ============================================================================
 * 辅助函数
 * ========================================================================== */

/**
 * 生成公司名称
 * 规则：只包含英文字母、数字和空格，长度1-50，必须包含至少一个英文字母，不能以空格开头
 *
 * @param fakerInstance - faker 实例
 * @returns 公司名称（英文）
 */
function generateCompanyName(fakerInstance: typeof faker): string {
  // 使用公司名称作为基础，但需要清理特殊字符
  const baseName = fakerInstance.company.name();

  // 移除所有非字母、数字、空格的字符
  let cleaned = baseName.replace(/[^a-zA-Z0-9\s]/g, " ");

  // 移除多余的空格
  cleaned = cleaned.replace(/\s+/g, " ");

  // 移除开头和结尾的空格
  cleaned = cleaned.trim();

  // 确保不为空且包含至少一个字母
  if (!cleaned || !/[a-zA-Z]/.test(cleaned)) {
    cleaned = "Company " + fakerInstance.string.numeric(5);
  }

  // 30%概率添加后缀
  if (fakerInstance.number.int({ min: 1, max: 10 }) <= 3) {
    const suffixes = ["Ltd", "Limited", "Corp", "Inc", "Group"];
    cleaned = cleaned + " " + fakerInstance.helpers.arrayElement(suffixes);
  }

  // 确保长度不超过50
  if (cleaned.length > 50) {
    cleaned = cleaned.substring(0, 50).trim();
  }

  return cleaned;
}

/**
 * 生成中文公司名称
 *
 * @param fakerInstance - faker 实例
 * @returns 公司名称（中文）
 */
function generateCompanyNameZh(fakerInstance: typeof faker): string {
  const prefixes = ["香港", "深圳", "上海", "北京", "广州", ""];
  const types = ["科技", "贸易", "金融", "实业", "投资", "咨询"];
  const suffixes = ["有限公司", "集团有限公司", "控股有限公司"];

  const prefix = fakerInstance.helpers.arrayElement(prefixes);
  const name = fakerInstance.helpers.arrayElement([
    "新世纪",
    "华泰",
    "恒大",
    "万通",
    "鼎盛",
    "博远",
    "诚信",
    "盛世",
    "龙腾",
    "瑞丰",
    "天成",
    "宏图",
    "锦绣",
    "嘉信",
    "腾飞",
    "远东",
  ]);
  const type = fakerInstance.helpers.arrayElement(types);
  const suffix = fakerInstance.helpers.arrayElement(suffixes);

  return `${prefix}${name}${type}${suffix}`;
}

/**
 * 生成银行账户名称
 * 规则：只包含英文字母、数字和空格，长度1-50，必须包含至少一个英文字母，不能以空格开头
 *
 * @param fakerInstance - faker 实例
 * @returns 银行账户名称
 */
function generateBankAccountName(fakerInstance: typeof faker): string {
  // 使用公司名称作为基础，但需要清理特殊字符
  const baseName = fakerInstance.company.name();

  // 移除所有非字母、数字、空格的字符
  let cleaned = baseName.replace(/[^a-zA-Z0-9\s]/g, " ");

  // 移除多余的空格
  cleaned = cleaned.replace(/\s+/g, " ");

  // 移除开头的空格
  cleaned = cleaned.trim();

  // 确保不为空且包含至少一个字母
  if (!cleaned || !/[a-zA-Z]/.test(cleaned)) {
    cleaned = "Company " + fakerInstance.string.numeric(5);
  }

  // 30%概率添加后缀
  if (fakerInstance.number.int({ min: 1, max: 10 }) <= 3) {
    const suffixes = ["Ltd", "Limited", "Corp", "Inc"];
    cleaned = cleaned + " " + fakerInstance.helpers.arrayElement(suffixes);
  }

  // 确保长度不超过50
  if (cleaned.length > 50) {
    cleaned = cleaned.substring(0, 50).trim();
  }

  return cleaned;
}

/**
 * 生成香港商业登记号码
 * 规则：只包含英文字母和数字，长度1-50
 *
 * @param fakerInstance - faker 实例
 * @returns 商业登记号码（格式：纯数字或字母数字组合）
 */
function generateHKBusinessRegNo(fakerInstance: typeof faker): string {
  // 生成8位数字 + 3位数字（不使用连字符）
  const part1 = fakerInstance.string.numeric(8);
  const part2 = fakerInstance.string.numeric(3);
  return `${part1}${part2}`;
}

/**
 * 生成注册成立证书号码
 *
 * @param fakerInstance - faker 实例
 * @param country - 国家代码
 * @returns 证书号码
 */
function generateIncorporationNo(
  fakerInstance: typeof faker,
  country: string
): string {
  if (country === "HKG") {
    // 香港：数字格式
    return fakerInstance.string.numeric(7);
  } else if (country === "CHN") {
    // 中国：统一社会信用代码（18位）
    return fakerInstance.string.alphanumeric({ length: 18, casing: "upper" });
  } else {
    // 其他国家：字母数字混合
    return fakerInstance.string.alphanumeric({ length: 10, casing: "upper" });
  }
}

/**
 * 生成注册地址
 *
 * @param fakerInstance - faker 实例
 * @param country - 国家代码
 * @returns 地址字符串
 */
function generateAddress(fakerInstance: typeof faker, country: string): string {
  if (country === "HKG") {
    const districts = [
      "Central",
      "Admiralty",
      "Wan Chai",
      "Causeway Bay",
      "Tsim Sha Tsui",
      "Mong Kok",
      "Kwun Tong",
      "Sha Tin",
      "Tuen Mun",
      "Yuen Long",
    ];
    const district = fakerInstance.helpers.arrayElement(districts);
    const building = fakerInstance.company.name() + " Tower";
    const floor = fakerInstance.number.int({ min: 1, max: 50 });
    const room = fakerInstance.number.int({ min: 1, max: 20 });

    return `Room ${room}, ${floor}/F, ${building}, ${district}, Hong Kong`;
  } else if (country === "CHN") {
    return `中国${fakerInstance.location.city()}${fakerInstance.location.street()}${fakerInstance.number.int({ min: 1, max: 999 })}号`;
  } else {
    return fakerInstance.location.streetAddress({ useFullAddress: true });
  }
}

/**
 * 生成业务电话（PhoneNumberInput组件需要的格式）
 *
 * @param fakerInstance - faker 实例
 * @param country - 国家代码
 * @returns 电话对象（包含区号和号码）
 */
function generateBusinessPhone(
  fakerInstance: typeof faker,
  country: string
): { phoneCountry: string; phoneNumber: string } {
  // 从真实的国家数据中获取电话区号
  const tel = COUNTRY_TEL_MAP.get(country) || "852";
  const phoneCountry = tel;

  // 生成8位数字号码
  const phoneNumber = fakerInstance.string.numeric(8);

  return {
    phoneCountry,
    phoneNumber,
  };
}

/* ============================================================================
 * 主函数
 * ========================================================================== */

/**
 * 生成企业信息表单模拟数据
 *
 * @param options - 生成配置选项
 * @returns 符合表单验证规则的模拟数据
 *
 * @example
 * ```typescript
 * // 生成基本企业信息
 * const basicData = generateCompanyInfoMockData();
 *
 * // 生成香港上市公司数据
 * const hkListedCompany = generateCompanyInfoMockData({
 *   registrationCountry: 'HKG',
 *   listingStatus: ListingStatusOptions.YES
 * });
 *
 * // 生成可重复的数据
 * const repeatableData = generateCompanyInfoMockData({
 *   seed: 12345
 * });
 * ```
 */
export function generateCompanyInfoMockData(
  options: CompanyMockDataGeneratorOptions = {}
): CompanyInfoFormData {
  const {
    registrationCountry: specifiedCountry,
    listingStatus: specifiedListingStatus,
    companyType: specifiedCompanyType,
    companyIndustry: specifiedIndustry,
    seed,
  } = options;

  // 创建 faker 实例
  const fakerInstance = seed !== undefined ? faker : faker;
  if (seed !== undefined) {
    fakerInstance.seed(seed);
  }

  // 确定注册国家（从允许和受限制的国家列表中随机选择）
  const registrationCountry =
    specifiedCountry ||
    fakerInstance.helpers.arrayElement(ALL_AVAILABLE_COUNTRIES).code2;

  // 生成公司名称
  const companyName = generateCompanyName(fakerInstance);

  // 根据国家决定是否生成中文名称
  const companyNameZh = generateCompanyNameZh(fakerInstance);

  // 银行账户名称（所有情况都生成，符合验证规则）
  const bankAccountName = generateBankAccountName(fakerInstance);

  // 香港商业登记号码（所有情况都生成，符合验证规则）
  const hkBusinessRegNo = generateHKBusinessRegNo(fakerInstance);

  // 确定上市状态
  const listingStatus =
    specifiedListingStatus ||
    fakerInstance.helpers.arrayElement(LISTING_STATUS_OPTIONS);

  // 确定公司类型
  const companyType =
    specifiedCompanyType ||
    fakerInstance.helpers.arrayElement(COMPANY_TYPE_OPTIONS);

  // 注册日期（1-20年前，格式：YYYY-MM-DD）
  const date = fakerInstance.date.past({
    years: 20,
  });
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const registrationDate = `${year}-${month}-${day}`;

  // 注册成立证书号码
  const incorporationNo = generateIncorporationNo(
    fakerInstance,
    registrationCountry
  );

  // 确定行业
  const companyIndustry =
    specifiedIndustry ||
    fakerInstance.helpers.arrayElement(COMMON_COMPANY_INDUSTRY_OPTIONS);

  // 生成地址
  const registeredAddress = generateAddress(fakerInstance, registrationCountry);

  // 业务地址（40%概率不同于注册地址）
  const businessAddress =
    fakerInstance.number.int({ min: 1, max: 10 }) <= 4
      ? generateAddress(fakerInstance, registrationCountry)
      : undefined;

  // 业务电话
  const businessPhone = generateBusinessPhone(
    fakerInstance,
    registrationCountry
  );

  // 电邮地址
  const email = fakerInstance.internet
    .email({
      provider: fakerInstance.helpers.arrayElement([
        "company.com",
        "business.com",
        "corp.com",
        "enterprise.com",
      ]),
    })
    .toLowerCase();

  // 企业网站（80%概率有）
  const website =
    fakerInstance.number.int({ min: 1, max: 10 }) <= 8
      ? `https://www.${fakerInstance.internet.domainName()}`
      : undefined;

  // 交易目的（1-3个）
  const purposeOfTransaction = fakerInstance.helpers.arrayElements(
    TRADING_PURPOSE_OPTIONS,
    fakerInstance.number.int({ min: 1, max: 3 })
  );

  return {
    companyName,
    companyNameZh,
    bankAccountName,
    hkBusinessRegNo,
    listingStatus,
    companyType,
    registrationCountry,
    registrationDate,
    incorporationNo,
    companyIndustry,
    registeredAddress,
    businessAddress,
    businessPhone,
    email,
    website,
    purposeOfTransaction,
  };
}

/**
 * 批量生成企业信息表单模拟数据
 *
 * @param count - 生成数据的数量
 * @param options - 生成配置选项
 * @returns 模拟数据数组
 *
 * @example
 * ```typescript
 * // 生成 10 条测试数据
 * const mockDataList = generateCompanyInfoMockDataBatch(10);
 * ```
 */
export function generateCompanyInfoMockDataBatch(
  count: number,
  options: CompanyMockDataGeneratorOptions = {}
): CompanyInfoFormData[] {
  return Array.from({ length: count }, (_, index) => {
    const itemOptions =
      options.seed !== undefined
        ? { ...options, seed: options.seed + index }
        : options;

    return generateCompanyInfoMockData(itemOptions);
  });
}

/**
 * 生成特定场景的模拟数据
 */
export const companyMockDataPresets = {
  /**
   * 香港上市公司
   */
  hkListedCompany: (): CompanyInfoFormData =>
    generateCompanyInfoMockData({
      registrationCountry: "HKG",
      listingStatus: ListingStatusOptions.YES,
      companyType: CompanyTypeOptions.FOR_PROFIT,
    }),

  /**
   * 香港非上市公司
   */
  hkPrivateCompany: (): CompanyInfoFormData =>
    generateCompanyInfoMockData({
      registrationCountry: "HKG",
      listingStatus: ListingStatusOptions.NO,
      companyType: CompanyTypeOptions.FOR_PROFIT,
    }),

  /**
   * 中国大陆公司
   */
  mainlandCompany: (): CompanyInfoFormData =>
    generateCompanyInfoMockData({
      registrationCountry: "CHN",
      listingStatus: ListingStatusOptions.NO,
      companyType: CompanyTypeOptions.FOR_PROFIT,
    }),

  /**
   * 新加坡公司
   */
  singaporeCompany: (): CompanyInfoFormData =>
    generateCompanyInfoMockData({
      registrationCountry: "SGP",
      listingStatus: ListingStatusOptions.NO,
      companyType: CompanyTypeOptions.FOR_PROFIT,
    }),

  /**
   * 非营利组织
   */
  nonProfitOrg: (): CompanyInfoFormData =>
    generateCompanyInfoMockData({
      companyType: CompanyTypeOptions.NON_PROFIT,
      listingStatus: ListingStatusOptions.NO,
    }),
};

/* ============================================================================
 * 导出
 * ========================================================================== */

export default {
  generateCompanyInfoMockData,
  generateCompanyInfoMockDataBatch,
  companyMockDataPresets,
};
